import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, Calendar, Building2, Files, File, Clock, Target, FileText, MapPin, EditIcon, FileDown, Users } from 'lucide-react';
import { Link } from '@tanstack/react-router';
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from '@/components/ui/breadcrumb';
import { toast } from 'react-toastify';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import MapboxMap, { parseCoords } from '@/ubication/mapbox';
import { TimezoneUtils } from '@/lib/utils/timezone';
import { RequestVisitService } from '@/lib/collections/VisitsLogs/FetchVisitLogs';
import { OfflineSyncBadge } from '@/components/offline/OfflineSyncBadge';
import { fetchUsersWithProfiles } from '@/lib/collections/workerProfile-Users/fetchCollaboratorsWithProfile';

interface VisitListLogsProps {
    userId: string;
}



const ListVisitLog = ({ userId }: VisitListLogsProps) => {
    const [documents, setDocuments] = useState<any[]>([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const now = new Date();
    const currentYear = now.getFullYear().toString();
    const currentMonth = String(now.getMonth() + 1).padStart(2, '0');
    const [statusFilter, setStatusFilter] = useState<string>('all'); // Default: pending

    const [usersMap, setUsersMap] = useState<Map<string, string>>(new Map());

    const [selectedYear, setSelectedYear] = useState<string>(currentYear);
    const [selectedMonth, setSelectedMonth] = useState<string>(currentMonth);
    const [searchQuery, setSearchQuery] = useState<string>('');
    const [page, setPage] = useState(1);
    const limit = 10;

    //generar doc
    const [generatingReport, setGeneratingReport] = useState(false);

    useEffect(() => {
        loadDocuments();
    }, [userId, selectedYear, selectedMonth, page, searchQuery, statusFilter]);


    useEffect(() => {
        fetchUsersWithProfiles().then(users => {
            const map = new Map(users.map(user => [user.id, user.name]));
            setUsersMap(map);
        });
    }, []);

    const getUserName = (id: string) => usersMap.get(id) || id;

    const getYearOptions = () => {
        const options = [];
        const currentYear = new Date().getFullYear();
        // Últimos 5 años
        for (let i = 0; i < 5; i++) {
            const year = currentYear - i;
            options.push({ value: year.toString(), label: year.toString() });
        }
        return options;
    };

    const getMonthOptions = () => {
        return [
            { value: '01', label: 'Enero' },
            { value: '02', label: 'Febrero' },
            { value: '03', label: 'Marzo' },
            { value: '04', label: 'Abril' },
            { value: '05', label: 'Mayo' },
            { value: '06', label: 'Junio' },
            { value: '07', label: 'Julio' },
            { value: '08', label: 'Agosto' },
            { value: '09', label: 'Septiembre' },
            { value: '10', label: 'Octubre' },
            { value: '11', label: 'Noviembre' },
            { value: '12', label: 'Diciembre' }
        ];
    };

    const loadDocuments = async () => {
        try {
            if (!searchQuery.trim()) {
                setLoading(true);
            }
            setError(null);
            let monthFilter: string | undefined = undefined;

            // Solo filtrar si ambos están seleccionados (no en "all")
            if (selectedYear !== 'all' && selectedMonth !== 'all') {
                monthFilter = `${selectedYear}-${selectedMonth}`;
            }

            const offset = (page - 1) * limit;
            const result = await RequestVisitService.getRequestVisitsByUser(
                userId,
                monthFilter,
                limit,
                offset,
                statusFilter
            );
            setDocuments(result.documents);
            // Filtrar por búsqueda si hay query
            let filteredDocs = result.documents;
            if (searchQuery.trim()) {
                const query = searchQuery.toLowerCase();
                filteredDocs = result.documents.filter(visitLog =>
                    visitLog.companyName?.toLowerCase().includes(query) ||
                    visitLog.visitPurpose?.toLowerCase().includes(query) ||
                    visitLog.contactPerson?.toLowerCase().includes(query) ||
                    visitLog.contactRole?.toLowerCase().includes(query)
                );
            }
            setDocuments(filteredDocs);

            setTotal(result.total);
        } catch {
            setError('No se pudo cargar la información. Intenta de nuevo.');
        } finally {
            setLoading(false);
        }
    };


    const totalPages = Math.ceil(total / limit);

    const parseVisits = (visits: string[]) => {
        try {
            return {
                registered: new Date(visits[0]),
                start: new Date(visits[1]),
                end: new Date(visits[2])
            };
        } catch {
            return null;
        }
    };

    if (loading && documents.length === 0) {
        return (
            <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
        );
    }

    const handleGenerateReport = async () => {
        let toastId: any = null;
        try {
            setGeneratingReport(true);
            setError(null);
            toastId = toast.loading('Generando reporte...');

            if (selectedYear === 'all' || selectedMonth === 'all') {
                throw new Error('Selecciona un año y mes específico');
            }

            const result = await RequestVisitService.generateMonthlyReport(
                userId,
                selectedYear,
                selectedMonth
            );

            if (!result.success) {
                throw new Error(result.error || 'Error generando reporte');
            }

            // Abrir documento en nueva pestaña
            const url = RequestVisitService.getReportUrl(result.fileId!);
            window.open(url);
            toast.dismiss(toastId);
            toast.success('¡Reporte generado exitosamente!');

        } catch (error: any) {
            if (toastId !== null) {
                toast.dismiss(toastId);
            }
            toast.error('Error al generar el reporte. Intenta de nuevo.');
            setError('No se pudo generar el reporte. Intenta de nuevo.');
        } finally {
            setGeneratingReport(false);
            if (toastId !== null) {
                toast.dismiss(toastId);
            }
        }
    };



    const getStatusBadge = (status: string) => {
        const variants = {
            pending: {
                color: 'bg-yellow-100 text-yellow-800 border-yellow-200',
                label: 'Pendiente'
            },
            completed: {
                color: 'bg-green-100 text-green-800 border-green-200',
                label: 'Completada'
            },
            canceled: {
                color: 'bg-red-100 text-red-800 border-red-200',
                label: 'Cancelada'
            }
        };

        const variant = variants[status as keyof typeof variants] || variants.pending;

        return (
            <span className={`${variant.color} border text-xs font-medium px-2 py-0.5 rounded-full whitespace-nowrap`}>
                {variant.label}
            </span>
        );
    };

    return (

        <><>
            <Breadcrumb>
                <BreadcrumbList>
                    <BreadcrumbItem>
                        <div className="flex items-center gap-1.5">
                            <Files />
                            <BreadcrumbPage>Bitácoras</BreadcrumbPage>
                        </div>
                    </BreadcrumbItem>
                    <BreadcrumbSeparator />
                    <BreadcrumbItem>
                        <BreadcrumbLink asChild>
                            <Link to="/requests">Menu</Link>
                        </BreadcrumbLink>
                    </BreadcrumbItem>
                    <BreadcrumbSeparator />
                    <BreadcrumbItem>
                        <BreadcrumbPage className="hidden sm:block">Bitácoras visita</BreadcrumbPage>
                        <BreadcrumbPage className="block sm:hidden "> <File className="w-5 h-5" /> </BreadcrumbPage>
                    </BreadcrumbItem>
                </BreadcrumbList>
            </Breadcrumb>
        </>

            <div className="space-y-6 pt-6">
                {/* Header con filtros */}
                <div className="flex flex-col gap-6">
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                        <div>
                            <h2 className="text-3xl font-bold bg-linear-to-r from-primary to-primary/60 bg-clip-text text-transparent">
                                Bitácoras de Visita
                            </h2>
                            <p className="text-sm text-muted-foreground mt-1">
                                {total} {total === 1 ? 'registro encontrado' : 'registros encontrados'}
                            </p>
                        </div>

                        <div className="flex gap-2">
                            {/* <Link to="/requests/$id/visit/addRequest" params={{ id: userId }}>
                                <Button className='cursor-pointer shadow-sm hover:shadow-md transition-shadow'>
                                    Agregar Registro
                                </Button>
                            </Link> */}

                            <OfflineSyncBadge />

                            <Button
                                onClick={handleGenerateReport}
                                className='cursor-pointer shadow-sm hover:shadow-md transition-shadow'
                                disabled={generatingReport || selectedYear === 'all' || selectedMonth === 'all'}
                                variant="outline"
                            >
                                {generatingReport ? (
                                    <>
                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                        Generando...
                                    </>
                                ) : (
                                    <>
                                        <FileDown className="mr-2 h-4 w-4" />
                                        Generar Documento
                                    </>
                                )}
                            </Button>
                        </div>
                    </div>

                    {/* Filtros */}

                    <div className="flex flex-wrap gap-3">
                        <div className="w-full sm:flex-1 sm:max-w-md">
                            <Input
                                type="text"
                                placeholder="Buscar por empresa, contacto, objetivo..."
                                value={searchQuery}
                                onChange={(e) => {
                                    setSearchQuery(e.target.value);
                                    setPage(1);
                                }}
                                className="w-full"
                            />
                        </div>

                        {/* Select Estado  */}
                        <Select value={statusFilter} onValueChange={(value) => {
                            setStatusFilter(value);
                            setPage(1);
                        }}>
                            <SelectTrigger className="w-40">
                                <SelectValue placeholder="Estado" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Todos los estados</SelectItem>
                                <SelectItem value="pending">Pendiente</SelectItem>
                                <SelectItem value="completed">Completada</SelectItem>
                                <SelectItem value="canceled">Cancelada</SelectItem>
                            </SelectContent>
                        </Select>

                        <Select value={selectedYear} onValueChange={(value) => {
                            setSelectedYear(value);
                            setPage(1);
                        }}>
                            <SelectTrigger className="w-32.5">
                                <SelectValue placeholder="Año" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Todos los años</SelectItem>
                                {getYearOptions().map(option => (
                                    <SelectItem key={option.value} value={option.value}>
                                        {option.label}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>

                        <Select value={selectedMonth} onValueChange={(value) => {
                            setSelectedMonth(value);
                            setPage(1);
                        }}>
                            <SelectTrigger className="w-35">
                                <SelectValue placeholder="Mes" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Todos los meses</SelectItem>
                                {getMonthOptions().map(option => (
                                    <SelectItem key={option.value} value={option.value}>
                                        {option.label}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                </div>

                {/* Error */}
                {error && (
                    <Alert variant="destructive">
                        <AlertDescription>{error}</AlertDescription>
                    </Alert>
                )}

                {/* Lista de documentos */}
                {documents.length === 0 ? (
                    <Card className="border-dashed">
                        <CardContent className="py-16 text-center">
                            <div className="flex flex-col items-center gap-4">
                                <div className="rounded-full bg-muted p-6">
                                    <Calendar className="h-12 w-12 text-muted-foreground" />
                                </div>
                                <div>
                                    <p className="text-lg font-medium">No hay solicitudes registradas</p>
                                    <p className="text-sm text-muted-foreground mt-1">
                                        {(selectedYear !== 'all' || selectedMonth !== 'all')
                                            ? 'Intenta ajustar los filtros de búsqueda'
                                            : 'Comienza agregando tu primera bitácora de visita'}
                                    </p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                ) : (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        {documents.map((visitLog) => {
                            const visits = parseVisits(visitLog.visits);
                            return (
                                <Card
                                    key={visitLog.$id}
                                    className="group hover:shadow-lg transition-all duration-300 border-l-4 border-l-primary/50 hover:border-l-primary overflow-hidden"
                                >
                                    <CardHeader className="bg-linear-to-r from-muted/30 to-transparent pb-3">
                                        <div className="flex flex-col lg:flex-row lg:justify-between lg:items-start gap-4">
                                            <div className="space-y-2 flex-1 min-w-0">
                                                <CardTitle className="flex items-start gap-3 text-xl">
                                                    <div className="rounded-lg bg-primary/10 p-2 mt-0.5 shrink-0">
                                                        <Building2 className="h-5 w-5 text-primary" />
                                                    </div>
                                                    <div className="flex-1 min-w-0 flex items-center gap-2 flex-wrap">
                                                        <span className="line-clamp-2 leading-tight wrap-break-word">
                                                            {visitLog.companyName.charAt(0).toUpperCase() + visitLog.companyName.slice(1).toLowerCase()}
                                                        </span>

                                                        {getStatusBadge(visitLog.status)}
                                                    </div>


                                                </CardTitle>
                                                <CardDescription className="flex items-center gap-2 text-base ">

                                                    {visitLog.usersId && visitLog.usersId.length > 0 && (
                                                        <div className="flex items-center gap-1.5 flex-wrap mt-1">
                                                            <Users className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                                                            {visitLog.usersId.map((id: string) => (
                                                                <span
                                                                    key={id}
                                                                    className="text-xs bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 px-2 py-0.5 rounded-full border border-slate-200 dark:border-slate-700"
                                                                >
                                                                    {getUserName(id)}
                                                                </span>
                                                            ))}
                                                        </div>
                                                    )}
                                                    <span className="font-medium">{visitLog.contactPerson}</span>
                                                    {visitLog.contactRole && (
                                                        <>
                                                            <span className="text-muted-foreground/50">•</span>
                                                            <span className="text-muted-foreground">{visitLog.contactRole}</span>
                                                        </>
                                                    )}
                                                </CardDescription>
                                            </div>
                                            <div className="flex gap-2 w-full lg:w-auto">
                                                <Dialog>
                                                    <DialogTrigger asChild>
                                                        <Button
                                                            size="sm"
                                                            className="flex-1 lg:flex-initial shrink-0 cursor-pointer shadow-sm hover:shadow-md transition-all"
                                                            variant="secondary"
                                                        >
                                                            <FileText className="mr-2 h-4 w-4" />
                                                            <span>Detalles</span>
                                                        </Button>
                                                    </DialogTrigger>
                                                    <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                                                        <DialogHeader>
                                                            <DialogTitle className="flex items-center gap-2">
                                                                <Building2 className="h-5 w-5" />
                                                                {visitLog.companyName}
                                                            </DialogTitle>
                                                            <DialogDescription>
                                                                {visitLog.contactPerson} {visitLog.contactRole && `· ${visitLog.contactRole}`}


                                                            </DialogDescription>
                                                        </DialogHeader>


                                                        <div className="overflow-y-auto max-h-[calc(80vh-120px)] pr-2">
                                                            {!visitLog ? (
                                                                <div className="py-12 text-center text-muted-foreground">Cargando...</div>
                                                            ) : (
                                                                <Tabs defaultValue="general" className="w-full">
                                                                    <TabsList className="grid w-full grid-cols-3">
                                                                        <TabsTrigger value="general">General</TabsTrigger>
                                                                        <TabsTrigger value="detalles">
                                                                            Detalles
                                                                            <span className="ml-2 text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full">
                                                                                {(visitLog.agreements?.length || 0) + (visitLog.observations?.length || 0)}
                                                                            </span>
                                                                        </TabsTrigger>
                                                                        <TabsTrigger value="firma">Firma</TabsTrigger>
                                                                    </TabsList>

                                                                    {/* ---------- GENERAL ---------- */}
                                                                    <TabsContent value="general" className="space-y-4 mt-4">
                                                                        <div className="grid gap-4">
                                                                            <div className="grid grid-cols-[120px_1fr] gap-2 items-start">
                                                                                <label className="text-sm font-medium text-muted-foreground">Empresa:</label>
                                                                                <p className="text-sm font-medium wrap-break-word">
                                                                                    {visitLog.companyName.charAt(0).toUpperCase() + visitLog.companyName.slice(1).toLowerCase()}
                                                                                </p>


                                                                            </div>
                                                                            {visitLog.usersId && visitLog.usersId.length > 0 && (
                                                                                <div className="grid grid-cols-[120px_1fr] gap-2 items-start">
                                                                                    <label className="text-sm font-medium text-muted-foreground">Acompañantes:</label>
                                                                                    <div className="flex flex-wrap gap-1.5">
                                                                                        {visitLog.usersId.slice(0, 3).map((id: string) => (
                                                                                            <span
                                                                                                key={id}
                                                                                                className="inline-flex items-center gap-1 text-xs bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 px-2 py-0.5 rounded-full border border-slate-200 dark:border-slate-700"
                                                                                            >
                                                                                                <Users className="h-3 w-3" />
                                                                                                {getUserName(id)}
                                                                                            </span>
                                                                                        ))}
                                                                                        {visitLog.usersId.length > 3 && (
                                                                                            <span className="inline-flex items-center text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full border border-primary/20">
                                                                                                +{visitLog.usersId.length - 3} más
                                                                                            </span>
                                                                                        )}
                                                                                    </div>
                                                                                </div>
                                                                            )}

                                                                            <div className="grid grid-cols-[120px_1fr] gap-2 items-start">
                                                                                <label className="text-sm font-medium text-muted-foreground">Sucursal:</label>
                                                                                <p className="text-sm font-medium wrap-break-word">{visitLog.branch || 'matriz'}</p>
                                                                            </div>


                                                                            <div className="grid grid-cols-[120px_1fr] gap-2 items-start">
                                                                                <label className="text-sm font-medium text-muted-foreground">Contacto:</label>
                                                                                <div className="text-sm min-w-0">
                                                                                    <p className="font-medium wrap-break-word">{visitLog.contactPerson}</p>
                                                                                    {visitLog.contactRole && (
                                                                                        <p className="text-muted-foreground wrap-break-word">{visitLog.contactRole}</p>
                                                                                    )}
                                                                                </div>
                                                                            </div>

                                                                            <div className="grid grid-cols-[120px_1fr] gap-2 items-start">
                                                                                <label className="text-sm font-medium text-muted-foreground">Objetivo:</label>
                                                                                <p className="text-sm leading-relaxed wrap-break-word whitespace-pre-wrap min-w-0">
                                                                                    {visitLog.visitPurpose}
                                                                                </p>
                                                                            </div>

                                                                            {parseVisits(visitLog.visits) && (
                                                                                <>
                                                                                    <div className="grid grid-cols-[120px_1fr] gap-2 items-start">
                                                                                        <label className="text-sm font-medium text-muted-foreground">Fecha:</label>
                                                                                        <p className="text-sm font-medium">
                                                                                            {TimezoneUtils.formatEcuadorDateOnly(parseVisits(visitLog.visits)!.start)}
                                                                                        </p>
                                                                                    </div>

                                                                                    <div className="grid grid-cols-[120px_1fr] gap-2 items-start">
                                                                                        <label className="text-sm font-medium text-muted-foreground">Horario:</label>
                                                                                        <p className="text-sm font-medium">
                                                                                            {TimezoneUtils.formatEcuadorTimeOnly(parseVisits(visitLog.visits)!.start)} -{" "}
                                                                                            {TimezoneUtils.formatEcuadorTimeOnly(parseVisits(visitLog.visits)!.end)}
                                                                                        </p>
                                                                                    </div>
                                                                                </>
                                                                            )}
                                                                        </div>
                                                                    </TabsContent>

                                                                    {/* ---------- DETALLES (ACUERDOS + OBSERVACIONES) ---------- */}
                                                                    <TabsContent value="detalles" className="space-y-4 mt-4 overflow-x-hidden">
                                                                        <div className="space-y-6">
                                                                            {/* Acuerdos */}
                                                                            <div className="overflow-x-hidden">
                                                                                <div className="flex items-center gap-2 mb-3">
                                                                                    <h3 className="text-sm font-semibold">Acuerdos</h3>
                                                                                    <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full">
                                                                                        {visitLog.agreements?.length || 0}
                                                                                    </span>
                                                                                </div>

                                                                                {visitLog.agreements && visitLog.agreements.length > 0 ? (
                                                                                    <div className="max-h-100 overflow-y-auto overflow-x-hidden space-y-2 pr-2">
                                                                                        {visitLog.agreements.map((agreement: string, index: number) => (
                                                                                            <div
                                                                                                key={index}
                                                                                                className="p-3 rounded-lg bg-muted/30 border border-border/50"
                                                                                            >
                                                                                                <div className="flex gap-3">
                                                                                                    <span className="text-xs font-bold text-muted-foreground mt-0.5 shrink-0">
                                                                                                        {index + 1}.
                                                                                                    </span>
                                                                                                    <p className="text-sm leading-relaxed min-w-0 wrap-break-word whitespace-pre-wrap">
                                                                                                        {agreement}
                                                                                                    </p>
                                                                                                </div>
                                                                                            </div>
                                                                                        ))}
                                                                                    </div>
                                                                                ) : (
                                                                                    <p className="text-sm text-muted-foreground italic">Sin acuerdos registrados</p>
                                                                                )}
                                                                            </div>

                                                                            {/* Observaciones */}
                                                                            <div className="overflow-x-hidden">
                                                                                <div className="flex items-center gap-2 mb-3">
                                                                                    <h3 className="text-sm font-semibold">Observaciones</h3>
                                                                                    <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full">
                                                                                        {visitLog.observations?.length || 0}
                                                                                    </span>
                                                                                </div>

                                                                                {visitLog.observations && visitLog.observations.length > 0 ? (
                                                                                    <div className="max-h-100 overflow-y-auto overflow-x-hidden space-y-2 pr-2">
                                                                                        {visitLog.observations.map((observation: string, index: number) => (
                                                                                            <div
                                                                                                key={index}
                                                                                                className="p-3 rounded-lg bg-muted/30 border border-border/50"
                                                                                            >
                                                                                                <div className="flex gap-3">
                                                                                                    <span className="text-xs font-bold text-muted-foreground mt-0.5 shrink-0">
                                                                                                        {index + 1}.
                                                                                                    </span>
                                                                                                    <p className="text-sm leading-relaxed min-w-0 wrap-break-word whitespace-pre-wrap">
                                                                                                        {observation}
                                                                                                    </p>
                                                                                                </div>
                                                                                            </div>
                                                                                        ))}
                                                                                    </div>
                                                                                ) : (
                                                                                    <p className="text-sm text-muted-foreground italic">Sin observaciones registradas</p>
                                                                                )}
                                                                            </div>
                                                                        </div>
                                                                    </TabsContent>

                                                                    {/* ---------- FIRMA ---------- */}
                                                                    <TabsContent value="firma" className="space-y-4 mt-4">
                                                                        {visitLog.signature && visitLog.signature.length > 0 ? (
                                                                            <div className="space-y-4">
                                                                                <div className="flex justify-center p-4 bg-muted/30 rounded-lg border">
                                                                                    <img
                                                                                        src={RequestVisitService.getSignatureUrl(visitLog.signature[0])}
                                                                                        alt="Firma"
                                                                                        className="max-w-full h-auto max-h-75 object-contain"
                                                                                    />
                                                                                </div>

                                                                                <div className="grid gap-3">
                                                                                    {visitLog.signature[2] && (
                                                                                        <div className="flex items-start gap-3 p-3 rounded-lg bg-muted/30">
                                                                                            <Calendar className="h-5 w-5 text-muted-foreground mt-0.5" />
                                                                                            <div>
                                                                                                <p className="text-xs text-muted-foreground mb-1">Fecha y hora</p>
                                                                                                <p className="text-sm font-medium">
                                                                                                    {TimezoneUtils.formatEcuadorDateOnly(new Date(visitLog.signature[2]))}
                                                                                                    {" a las "}
                                                                                                    {TimezoneUtils.formatEcuadorTimeOnly(new Date(visitLog.signature[2]))}
                                                                                                </p>
                                                                                            </div>
                                                                                        </div>
                                                                                    )}

                                                                                    {visitLog.signature[1] && (
                                                                                        <Dialog>
                                                                                            <DialogTrigger asChild>
                                                                                                <button className="flex items-start gap-3 p-3 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors text-left w-full cursor-pointer">
                                                                                                    <MapPin className="h-5 w-5 text-blue-500 mt-0.5" />
                                                                                                    <div className="flex-1">
                                                                                                        <p className="text-xs text-muted-foreground mb-1">Ubicación de firma</p>
                                                                                                        <p className="text-sm font-medium text-blue-500 underline">Ver en mapa</p>
                                                                                                        <p className="text-xs text-muted-foreground mt-1 font-mono">{visitLog.signature[1]}</p>
                                                                                                    </div>
                                                                                                </button>
                                                                                            </DialogTrigger>

                                                                                            <DialogContent className="w-full max-w-2xl">
                                                                                                <DialogHeader>
                                                                                                    <DialogTitle>Ubicación de la Firma</DialogTitle>
                                                                                                </DialogHeader>

                                                                                                <MapboxMap coords={parseCoords(visitLog.signature[1])} height="h-96" />
                                                                                            </DialogContent>
                                                                                        </Dialog>
                                                                                    )}
                                                                                </div>
                                                                            </div>
                                                                        ) : (
                                                                            <div className="text-center py-8 text-muted-foreground">
                                                                                <p className="italic">Sin firma registrada</p>
                                                                            </div>
                                                                        )}
                                                                    </TabsContent>
                                                                </Tabs>
                                                            )}
                                                        </div>

                                                    </DialogContent>
                                                </Dialog>

                                                <Link to="/visitLog/$id/$visitId/addVisitLog" params={{ id: userId, visitId: visitLog.$id }}
                                                    className="flex-1 lg:flex-initial">
                                                    <Button
                                                        size="sm"
                                                        className="w-full shrink-0 cursor-pointer shadow-sm hover:shadow-md transition-all"
                                                        variant="outline"
                                                    >
                                                        <EditIcon className="mr-2 h-4 w-4" />
                                                        <span>Bitácora</span>
                                                    </Button>
                                                </Link>
                                            </div>
                                        </div>
                                    </CardHeader>

                                    <CardContent className="">
                                        <div className="grid sm:grid-cols-2 gap-6">
                                            {/* Columna izquierda */}
                                            <div className="space-y-4">
                                                <div className="flex items-start gap-3">
                                                    <div className="rounded-md bg-blue-500/10 p-2 shrink-0">
                                                        <Target className="h-4 w-4 text-blue-600" />
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <p className="text-xs font-medium text-muted-foreground mb-1">Objetivo</p>
                                                        <p className="text-sm font-medium wrap-break-word overflow-ellipsis overflow-hidden whitespace-nowrap">
                                                            {visitLog.visitPurpose.length > 100 ? `${visitLog.visitPurpose.slice(0, 35)}...` : visitLog.visitPurpose}

                                                        </p>
                                                    </div>
                                                </div>

                                                {visits && (
                                                    <div className="flex items-start gap-3">
                                                        <div className="rounded-md bg-green-500/10 p-2 shrink-0">
                                                            <Calendar className="h-4 w-4 text-green-600" />
                                                        </div>
                                                        <div className="flex-1">
                                                            <p className="text-xs font-medium text-muted-foreground mb-1">Fecha de visita</p>
                                                            <p className="text-sm font-medium">
                                                                {TimezoneUtils.formatEcuadorDateOnly(visits.start)}
                                                            </p>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>

                                            {/* Columna derecha */}
                                            <div className="space-y-4">
                                                {visits && (
                                                    <div className="flex items-start gap-3">
                                                        <div className="rounded-md bg-purple-500/10 p-2 shrink-0">
                                                            <Clock className="h-4 w-4 text-purple-600" />
                                                        </div>
                                                        <div className="flex-1">
                                                            <p className="text-xs font-medium text-muted-foreground mb-1">Horario</p>
                                                            <p className="text-sm font-medium">
                                                                {TimezoneUtils.formatEcuadorTimeOnly(visits.start)}
                                                                {' - '}
                                                                {TimezoneUtils.formatEcuadorTimeOnly(visits.end)}
                                                            </p>
                                                        </div>
                                                    </div>
                                                )}

                                                <div className="flex gap-3">
                                                    <div className="flex-1 rounded-lg bg-linear-to-br from-orange-500/10 to-orange-500/5 p-3 border border-orange-200/50">
                                                        <div className="flex items-center justify-between">
                                                            <p className="text-xs font-medium text-muted-foreground">Acuerdos</p>
                                                            <span className="inline-flex items-center justify-center rounded-full bg-orange-500/20 px-2.5 py-0.5 text-sm font-semibold text-orange-700">
                                                                {visitLog.agreements?.length || 0}
                                                            </span>
                                                        </div>
                                                    </div>

                                                    <div className="flex-1 rounded-lg bg-linear-to-br from-cyan-500/10 to-cyan-500/5 p-3 border border-cyan-200/50">
                                                        <div className="flex items-center justify-between">
                                                            <p className="text-xs font-medium text-muted-foreground">Observaciones</p>
                                                            <span className="inline-flex items-center justify-center rounded-full bg-cyan-500/20 px-2.5 py-0.5 text-sm font-semibold text-cyan-700">
                                                                {visitLog.observations?.length || 0}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </CardContent>
                                </Card>
                            );
                        })}
                    </div>
                )}

                {/* Paginación */}
                {totalPages > 1 && (
                    <div className="flex justify-center items-center gap-3 pt-2">
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setPage(p => Math.max(1, p - 1))}
                            disabled={page === 1 || loading}
                            className="shadow-sm"
                        >
                            Anterior
                        </Button>
                        <div className="flex items-center gap-2 px-4 py-2 bg-muted rounded-md">
                            <span className="text-sm font-medium">Página {page}</span>
                            <span className="text-muted-foreground">de</span>
                            <span className="text-sm font-medium">{totalPages}</span>
                        </div>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                            disabled={page === totalPages || loading}
                            className="shadow-sm"
                        >
                            Siguiente
                        </Button>
                    </div>
                )}
            </div></>
    );
};

export default ListVisitLog;